/**
 * React Query hooks for Exercises
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  exercisesApi,
  type Exercise,
  type ExercisePlan,
  type ExerciseSession,
  type ExercisePlanCreate,
  type ExerciseSessionCreate,
  type CustomExercisePlanCreate,
  type CustomExercisePlanUpdate,
  type CustomExercisePlan,
} from '../api/exercises';

// === EXERCISE LIBRARY HOOKS ===

/**
 * Hook to get exercise library
 */
export function useExercises(params?: {
  category?: string;
  difficulty?: string;
  search?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['exercises', params],
    queryFn: () => exercisesApi.getExercises(params),
    retry: false, // ✅ FIX: Don't retry on auth errors
  });
}

/**
 * Hook to get single exercise
 */
export function useExercise(exerciseId: number) {
  return useQuery({
    queryKey: ['exercise', exerciseId],
    queryFn: () => exercisesApi.getExercise(exerciseId),
    enabled: !!exerciseId && exerciseId > 0,
    retry: false, // ✅ FIX: Don't retry on auth errors
  });
}

// === EXERCISE PLAN HOOKS ===

/**
 * Hook to get MY exercise plans (for current patient user)
 * Uses /me endpoint - no patientId required
 */
export function useMyExercisePlans(activeOnly: boolean = true) {
  return useQuery({
    queryKey: ['myExercisePlans', activeOnly],
    queryFn: () => exercisesApi.getMyExercisePlans(activeOnly),
    retry: false, // ✅ FIX: Don't retry on auth errors
  });
}

/**
 * Hook to get patient exercise plans (standard plans from library)
 * For use by doctors, therapists, coordinators, nurses (requires patientId)
 */
export function usePatientExercisePlans(patientId: number, activeOnly: boolean = true) {
  return useQuery({
    queryKey: ['exercisePlans', patientId, activeOnly],
    queryFn: () => exercisesApi.getPatientExercisePlans(patientId, activeOnly),
    enabled: !!patientId && patientId > 0, // ✅ FIX: Only fetch when we have a valid patient ID
    retry: false, // ✅ FIX: Don't retry on auth errors
  });
}

/**
 * Hook to get ALL MY exercise plans (both standard and custom) for current patient
 * Uses /me endpoints - no patientId required
 */
export function useMyAllExercisePlans(activeOnly: boolean = true) {
  const standardPlans = useMyExercisePlans(activeOnly);
  const customPlans = useMyCustomExercisePlans(activeOnly);

  return {
    data: {
      standardPlans: standardPlans.data?.plans || [],
      customPlans: customPlans.data?.exercise_plans || [],
      allPlans: [
        ...(standardPlans.data?.plans || []),
        ...(customPlans.data?.exercise_plans || [])
      ],
      count: (standardPlans.data?.plans?.length || 0) + (customPlans.data?.exercise_plans?.length || 0)
    },
    isLoading: standardPlans.isLoading || customPlans.isLoading,
    error: standardPlans.error || customPlans.error,
    refetch: () => {
      standardPlans.refetch();
      customPlans.refetch();
    }
  };
}

/**
 * Hook to get ALL patient exercise plans (both standard and custom)
 * For use by doctors, therapists, coordinators, nurses (requires patientId)
 */
export function useAllPatientExercisePlans(patientId: number, activeOnly: boolean = true) {
  const standardPlans = usePatientExercisePlans(patientId, activeOnly);
  
  // Use the existing usePatientCustomExercisePlans hook defined later in this file
  const customPlans = useQuery({
    queryKey: ['customExercisePlans', patientId, activeOnly],
    queryFn: () => exercisesApi.getPatientCustomExercisePlans(patientId, activeOnly),
    enabled: !!patientId && patientId > 0,
    retry: false,
  });

  return {
    data: {
      standardPlans: standardPlans.data?.plans || [],
      customPlans: customPlans.data?.exercise_plans || [],
      allPlans: [
        ...(standardPlans.data?.plans || []),
        ...(customPlans.data?.exercise_plans || [])
      ],
      count: (standardPlans.data?.plans?.length || 0) + (customPlans.data?.exercise_plans?.length || 0)
    },
    isLoading: standardPlans.isLoading || customPlans.isLoading,
    error: standardPlans.error || customPlans.error,
    refetch: () => {
      standardPlans.refetch();
      customPlans.refetch();
    }
  };
}

/**
 * Hook to create exercise plan
 */
export function useCreateExercisePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ExercisePlanCreate) => exercisesApi.createExercisePlan(data),
    onSuccess: (response, variables) => {
      toast.success('Exercise plan created!', {
        description: `Prescribed: ${response.exercise_name}`,
      });
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['exercisePlans', variables.patient_id] });
      queryClient.invalidateQueries({ queryKey: ['patientDashboard', variables.patient_id] });
    },
    onError: (error: any) => {
      toast.error('Failed to create exercise plan', {
        description: error.message || 'Please try again',
      });
    },
  });
}

/**
 * Hook to update exercise plan
 */
export function useUpdateExercisePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      planId,
      data,
    }: {
      planId: number;
      data: { is_active?: boolean; notes?: string };
    }) => exercisesApi.updateExercisePlan(planId, data),
    onSuccess: () => {
      toast.success('Exercise plan updated successfully');
      
      // Invalidate all exercise plan queries
      queryClient.invalidateQueries({ queryKey: ['exercisePlans'] });
    },
    onError: (error: any) => {
      toast.error('Failed to update exercise plan', {
        description: error.message || 'Please try again',
      });
    },
  });
}

/**
 * Hook to delete exercise plan
 */
export function useDeleteExercisePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, patientId }: { planId: number; patientId: number }) => 
      exercisesApi.deleteExercisePlan(planId),
    onSuccess: () => {
      toast.success('Exercise plan deleted successfully');
      
      // Invalidate all exercise plan queries
      queryClient.invalidateQueries({ queryKey: ['exercisePlans'] });
      queryClient.invalidateQueries({ queryKey: ['patientDashboard'] });
    },
    onError: (error: any) => {
      toast.error('Failed to delete exercise plan', {
        description: error.message || 'Please try again',
      });
    },
  });
}

// === EXERCISE SESSION HOOKS ===

/**
 * Hook to log exercise session
 */
export function useLogExerciseSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ExerciseSessionCreate) => exercisesApi.logExerciseSession(data),
    onSuccess: (response, variables) => {
      toast.success('Exercise session logged!', {
        description: 'Great work on completing your exercise!',
      });
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['exerciseSessions', variables.patient_id] });
      queryClient.invalidateQueries({ queryKey: ['exerciseStats', variables.patient_id] });
      queryClient.invalidateQueries({ queryKey: ['patientDashboard', variables.patient_id] });
      queryClient.invalidateQueries({ queryKey: ['adherence', variables.patient_id] });
    },
    onError: (error: any) => {
      toast.error('Failed to log exercise session', {
        description: error.message || 'Please try again',
      });
    },
  });
}

/**
 * Hook to get patient exercise sessions
 */
export function usePatientExerciseSessions(
  patientId: number,
  params?: { days?: number; limit?: number }
) {
  return useQuery({
    queryKey: ['exerciseSessions', patientId, params],
    queryFn: () => exercisesApi.getPatientExerciseSessions(patientId, params),
    enabled: !!patientId && patientId > 0, // ✅ FIX: Only fetch when we have a valid patient ID
    retry: false, // ✅ FIX: Don't retry on auth errors
  });
}

/**
 * Hook to update exercise session
 */
export function useUpdateExerciseSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      data,
    }: {
      sessionId: number;
      data: ExerciseSessionCreate;
    }) => exercisesApi.updateExerciseSession(sessionId, data),
    onSuccess: (response, variables) => {
      toast.success('Exercise session updated successfully');
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['exerciseSessions'] });
      queryClient.invalidateQueries({ queryKey: ['exerciseStats'] });
      queryClient.invalidateQueries({ queryKey: ['patientDashboard'] });
    },
    onError: (error: any) => {
      toast.error('Failed to update exercise session', {
        description: error.message || 'Please try again',
      });
    },
  });
}

/**
 * Hook to delete exercise session
 */
export function useDeleteExerciseSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: number) => exercisesApi.deleteExerciseSession(sessionId),
    onSuccess: (response) => {
      toast.success('Exercise session deleted successfully');
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['exerciseSessions'] });
      queryClient.invalidateQueries({ queryKey: ['exerciseStats'] });
      queryClient.invalidateQueries({ queryKey: ['patientDashboard'] });
      queryClient.invalidateQueries({ queryKey: ['adherence'] });
    },
    onError: (error: any) => {
      toast.error('Failed to delete exercise session', {
        description: error.message || 'Please try again',
      });
    },
  });
}

/**
 * Hook to get exercise statistics
 */
export function useExerciseStats(patientId: number, days: number = 7) {
  return useQuery({
    queryKey: ['exerciseStats', patientId, days],
    queryFn: () => exercisesApi.getExerciseStats(patientId, days),
    enabled: !!patientId,
  });
}

// === UTILITY HOOKS ===

/**
 * Hook to get scheduled exercises for today
 */
export function useTodayExercises(patientId: number) {
  const { data: plansData, isLoading: plansLoading } = usePatientExercisePlans(patientId, true);
  const { data: sessionsData, isLoading: sessionsLoading } = usePatientExerciseSessions(
    patientId,
    { days: 1 }
  );

  return {
    scheduledExercises: plansData?.plans || [],
    completedToday: sessionsData?.sessions || [],
    isLoading: plansLoading || sessionsLoading,
  };
}

/**
 * Hook to get exercise completion rate
 */
export function useExerciseCompletionRate(patientId: number, days: number = 7) {
  const { data, isLoading } = useExerciseStats(patientId, days);

  return {
    completionRate: data?.completion_rate || 0,
    totalSessions: data?.total_sessions || 0,
    completedSessions: data?.completed_sessions || 0,
    isLoading,
  };
}

// === CUSTOM EXERCISE PLAN HOOKS ===

/**
 * Hook to get MY custom exercise plans (for current patient user)
 * Uses /me endpoint - no patientId required
 */
export function useMyCustomExercisePlans(activeOnly: boolean = false) {
  return useQuery({
    queryKey: ['myCustomExercisePlans', activeOnly],
    queryFn: () => exercisesApi.getMyCustomExercisePlans(activeOnly),
    retry: false, // ✅ FIX: Don't retry on auth errors
  });
}

/**
 * Hook to get patient custom exercise plans
 * For use by doctors, therapists, coordinators, nurses (requires patientId)
 */
export function usePatientCustomExercisePlans(patientId: number, activeOnly: boolean = false) {
  return useQuery({
    queryKey: ['customExercisePlans', patientId, activeOnly],
    queryFn: () => exercisesApi.getPatientCustomExercisePlans(patientId, activeOnly),
    enabled: !!patientId && patientId > 0, // ✅ FIX: Only fetch when we have a valid patient ID
    retry: false, // ✅ FIX: Don't retry on auth errors
  });
}

/**
 * Hook to create custom exercise plan
 */
export function useCreateCustomExercisePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CustomExercisePlanCreate) => exercisesApi.createCustomExercisePlan(data),
    onSuccess: (response, variables) => {
      toast.success('Exercise plan created!', {
        description: `Created plan with ${response.exercise_count} exercises`,
      });
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['customExercisePlans', variables.patient_id] });
      queryClient.invalidateQueries({ queryKey: ['patientDashboard', variables.patient_id] });
    },
    onError: (error: any) => {
      toast.error('Failed to create exercise plan', {
        description: error.message || 'Please try again',
      });
    },
  });
}

/**
 * Hook to update custom exercise plan
 */
export function useUpdateCustomExercisePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      planId,
      data,
    }: {
      planId: number;
      data: CustomExercisePlanUpdate;
    }) => exercisesApi.updateCustomExercisePlan(planId, data),
    onSuccess: () => {
      toast.success('Exercise plan updated successfully');
      
      // Invalidate all custom exercise plan queries
      queryClient.invalidateQueries({ queryKey: ['customExercisePlans'] });
    },
    onError: (error: any) => {
      toast.error('Failed to update exercise plan', {
        description: error.message || 'Please try again',
      });
    },
  });
}

/**
 * Hook to log manual exercise (not from a prescribed plan)
 * NOTE: Backend endpoint returns 501 Not Implemented
 * Use this only when backend implements manual logging
 */
export function useLogManualExercise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      patient_id: number;
      exercise_name: string;
      duration_seconds: number;
      intensity?: number;
      notes?: string;
      completed_date?: string;
    }) => exercisesApi.logManualExercise(data),
    onSuccess: (response, variables) => {
      toast.success('Exercise logged!', {
        description: 'Your exercise has been recorded.',
      });
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['exerciseSessions', variables.patient_id] });
      queryClient.invalidateQueries({ queryKey: ['exerciseStats', variables.patient_id] });
      queryClient.invalidateQueries({ queryKey: ['patientDashboard', variables.patient_id] });
      queryClient.invalidateQueries({ queryKey: ['adherence', variables.patient_id] });
    },
    onError: (error: any) => {
      // Backend returns 501 for manual logging
      if (error.status === 501) {
        toast.error('Manual exercise logging not available', {
          description: 'Please complete exercises from your prescribed exercise plan.',
        });
      } else {
        toast.error('Failed to log exercise', {
          description: error.message || 'Please try again',
        });
      }
    },
  });
}